/**
 * ユーザーのWebサイトに埋め込みでAI面接の動画を表示させるためのスクリプト
 * 同一ページに複数のスクリプトを埋め込むことが可能
 *
 * STUDIO 等の sandbox iframe 経由で埋め込まれた場合は postMessage Bridge により
 * 親ページ常駐の parent-bridge.js にモーダル展開を委譲する（bridge mode）。
 * 親に bridge が居ない場合は従来通り iframe 内でモーダルを開く（inline-fallback mode）。
 */
import EmbeddingAiInterview from '../src/components/embedding/EmbeddingAiInterview'
import { BridgeProvider, type BridgeSharedData } from '../src/components/embedding/bridge/BridgeContext'
import { isInlineDisplayType } from '../src/lib/displayType'
import {
  detectMode,
  listenForLateAck,
  sendOpenIntent,
  sendPrepareIntent,
  type EmbeddedMode,
} from '../src/lib/embedding/embedded-mode'
import { generateInstanceId } from '../src/lib/embedding/protocol'
import { buildWatermarkUrl } from '../src/lib/watermarkLink'
import embeddedRender from './utils/embeddedRender'
;(() => {
  // IIFEの先頭で currentScript をキャプチャ（スクリプト実行中のみ有効）
  const thisScript = document.currentScript ?? document.getElementById('gai-agents-embed-script')

  // HLSプレイヤーが hls.min.js を自己ホスト(studioオリジン)から遅延ロードできるよう、
  // アセットのオリジンをグローバルに設定する（src/lib/hlsPlayer.ts が参照）。
  const embedScriptSrc = (thisScript as HTMLScriptElement | null)?.src || ''
  ;(window as Window & { __MOOVY_ASSET_ORIGIN__?: string }).__MOOVY_ASSET_ORIGIN__ = embedScriptSrc
    ? new URL(embedScriptSrc).origin
    : 'https://studio.moovy.jp'

  // 初回アクセス時のネットワーク遅延を削減するため、主要ドメインに preconnect する。
  // スクリプト実行の最初期で注入することで、DNS/TLSハンドシェイクを並列化し
  // 後続の API / CDN / フォント取得の Waterfall を短縮する（LCPを200-400ms短縮）。
  injectResourceHints()

  /**
   * Execution
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', main)
  } else {
    main()
  }

  /**
   * Functions
   */

  // インスタンス毎にアクティブなコンテナを保持する。
  // late-ack を受信して bridge mode にアップグレードする際、
  // 既存コンテナを取り外して新しいツリーを再マウントするのに使う。
  let activeContainer: HTMLElement | null = null

  function main() {
    const scenarioId = getScenarioId()
    if (!scenarioId) return

    if (!thisScript) {
      console.error('GAI_AGENTS: スクリプトタグが見つかりません')
      return
    }

    // `data-tenant-id` が与えられていれば scenarios / tenants を並列取得する。
    // 無ければ従来どおり scenario レスポンスの tenantId で直列フォールバック。
    const tenantId = thisScript.getAttribute('data-tenant-id') ?? undefined
    // `data-mode="single"` + `data-video-id="..."`: 単独サムネイル埋め込みScript。
    // 単独サムネイル表示し、クリック時にプレイリスト全体を切替視聴可能なモーダルを開く。
    const mode = thisScript.getAttribute('data-mode') ?? undefined
    const modeVideoId = thisScript.getAttribute('data-video-id') ?? undefined
    // `data-frame-embed="true"`: iframe 埋め込みページ（/embed/{scenarioId}）が出す専用フラグ。
    // 顧客が貼る script タグには付かない。モーダルの間だけ iframe を全画面化する挙動を有効にする。
    const frameEmbed = thisScript.getAttribute('data-frame-embed') === 'true'
    const instanceId = generateInstanceId()

    // モード判定：iframe 内なら親に ping を投げて DEFAULT_HANDSHAKE_TIMEOUT_MS（100ms）待つ。
    // direct（親ページ直接）なら同期的に解決される。
    detectMode({ instanceId }).then((embeddedMode) => {
      proceed(scenarioId, tenantId, instanceId, embeddedMode, mode, modeVideoId, frameEmbed)

      // inline-fallback で確定した場合、parent-bridge.js が遅延ロードされる
      // 可能性に備えて、追加で 30 秒間 ack を待ち受ける。
      // 遅延 ack が到着したら bridge mode に再マウントする。
      if (embeddedMode === 'inline-fallback') {
        listenForLateAck({
          instanceId,
          onAck: () => {
            // 既存コンテナをクリーンアップして bridge mode で再 render
            if (activeContainer) {
              activeContainer.remove()
              activeContainer = null
            }
            proceed(scenarioId, tenantId, instanceId, 'bridge', mode, modeVideoId, frameEmbed)
          },
        })
      }
    })
  }

  function proceed(
    scenarioId: string,
    tenantId: string | undefined,
    instanceId: string,
    embeddedMode: EmbeddedMode,
    mode: string | undefined,
    modeVideoId: string | undefined,
    frameEmbed: boolean,
  ) {
    if (!thisScript) return

    // コンテナ要素を作成（ユニークIDで複数インスタンスに対応）
    const container = document.createElement('div')
    container.id = `gai-agents-embed-container-${Math.random().toString(36).slice(-8)}`
    activeContainer = container

    // スクリプトタグがheadタグ内にある場合はbodyタグの末尾に追加
    // bodyタグ内にある場合はスクリプトタグの直後に追加
    const isInHead = thisScript.closest('head') !== null
    const displayHint: 'popup' | 'inline' = isInHead ? 'popup' : 'inline'

    if (isInHead) {
      // headタグ内の場合、bodyタグの末尾に追加（POPUP型を想定）
      document.body.appendChild(container)
      renderEmbed(container, scenarioId, tenantId, instanceId, embeddedMode, displayHint, mode, modeVideoId, frameEmbed)
      return
    }

    // bodyタグ内の場合、スクリプトタグの直後に追加（インライン表示を想定）
    thisScript.parentNode?.insertBefore(container, thisScript.nextSibling)

    // Light DOM に「Powered by moovy」リンクを注入する
    // Shadow DOM 内のリンクは Googlebot に被リンクとして評価されないため、
    // Light DOM に <a> を配置する。同一ページに複数埋め込まれても 1 つだけ出力する。
    const creditLink = injectCreditLink(container, tenantId)

    // ビューポート外ならスクロールで近づいた時点で初期化することで、
    // above-the-fold のレンダーを邪魔せず、ネットワーク帯域も節約する。
    //
    // iframe 埋め込みページでは可視判定を待たない。クロスオリジン iframe 内の
    // IntersectionObserver は「親ページのビューポートに実際に入っているか」で判定され
    // rootMargin が効かないため、背の高い iframe（タイル型で本数が多い等）だと上端が
    // 画面外にある間ずっと描画されない。読み込みの遅延は iframe タグ側の loading="lazy"
    // が担っているので、ページが動いた時点で描画してよい。
    const renderNow: typeof renderWhenVisible = frameEmbed ? (_target, callback) => callback() : renderWhenVisible
    renderNow(container, () =>
      renderEmbed(
        container,
        scenarioId,
        tenantId,
        instanceId,
        embeddedMode,
        displayHint,
        mode,
        modeVideoId,
        frameEmbed,
        // displayType 確定時にクレジット配置を補正する。
        // SLIDER / TILE / BOX / WIDE はその場にインライン表示されるためスクリプト位置のままで自然。
        // POPUP は本体が position:fixed で画面隅に切り離されるため、
        // クレジットも fixed 配置へ補正してスクリプト位置への取り残しを防ぐ。
        (displayType) => {
          const isInline = isInlineDisplayType(displayType)
          if (creditLink) {
            // 表示タイプが確定したので、流入計測の utm_content を掲載形態まで絞り込む
            creditLink.href = buildWatermarkUrl(isInline ? 'embed_inline' : 'embed_popup', { tenantId })
          }
          if (!isInline && creditLink) applyPopupCreditPlacement(creditLink)
          if (
            isInline &&
            creditLink &&
            (displayType === 'BOX' || displayType === 'WIDE' || displayType === 'WIDE_SINGLE')
          ) {
            applyPolishedInlineCreditPlacement(creditLink)
          }
        },
      ),
    )
  }

  function renderEmbed(
    container: HTMLElement,
    scenarioId: string,
    tenantId: string | undefined,
    instanceId: string,
    embeddedMode: EmbeddedMode,
    displayHint: 'popup' | 'inline',
    mode: string | undefined,
    modeVideoId: string | undefined,
    frameEmbed: boolean,
    onDisplayResolved?: (displayType: string | undefined) => void,
  ) {
    const interview = (
      <EmbeddingAiInterview
        scenarioId={scenarioId}
        tenantId={tenantId}
        mode={mode}
        modeVideoId={modeVideoId}
        frameEmbed={frameEmbed}
        onDisplayResolved={onDisplayResolved}
      />
    )

    // bridge mode: サムネクリックを親に postMessage で委譲する Provider でラップ。
    // direct / inline-fallback mode: 従来どおり Provider なしで render。
    //
    // 案C: SWR cache 共有
    // EmbeddingAiInterview が SWR 解決時に setSharedData を呼ぶと、scenario/tenant を
    // クロージャ変数に保持する。次に sendOpenIntent が走った時に postMessage の
    // payload に同梱し、親側で SWR cache を pre-populate して再 fetch を省く。
    //
    // 案E: hidden pre-mount
    // setSharedData のタイミングで親に PREPARE メッセージを送信し、親に hidden で
    // EmbeddingAiInterview を mount しておくよう依頼する。クリック時の OPEN は
    // 既存ツリーへの軽い open() 呼び出しで済むため重い React mount が事前完了する。
    // PREPARE は instanceId / scenarioId 単位で重複送信しても害がないが、
    // 念のため送信済みかをガードする。
    let sharedData: BridgeSharedData | undefined
    let hasSentPrepare = false
    const tree =
      embeddedMode === 'bridge' ? (
        <BridgeProvider
          value={{
            sendOpenIntent: (videoId) => {
              sendOpenIntent(
                {
                  scenarioId,
                  tenantId,
                  videoId,
                  scenario: sharedData?.scenario,
                  tenant: sharedData?.tenant,
                },
                instanceId,
              )
            },
            setSharedData: (data) => {
              sharedData = data
              if (!hasSentPrepare) {
                hasSentPrepare = true
                sendPrepareIntent(
                  {
                    scenarioId,
                    tenantId,
                    scenario: data.scenario,
                    tenant: data.tenant,
                  },
                  instanceId,
                )
              }
            },
          }}
        >
          {interview}
        </BridgeProvider>
      ) : (
        interview
      )

    embeddedRender(tree, {
      parent: container,
      scriptElement: thisScript as HTMLScriptElement,
      displayHint,
    })
  }

  /**
   * 要素がビューポートに入ってから callback を実行する。
   * IntersectionObserver が未サポートのブラウザでは即時実行。
   * rootMargin を広めに取り、ユーザーが到達する手前で先読みを開始する。
   */
  function renderWhenVisible(target: Element, callback: () => void) {
    if (typeof IntersectionObserver === 'undefined') {
      callback()
      return
    }
    const io = new IntersectionObserver(
      (entries, observer) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            observer.disconnect()
            callback()
            return
          }
        }
      },
      { rootMargin: '400px 0px' },
    )
    io.observe(target)
  }

  /**
   * API/CDN/フォントドメインへの preconnect / dns-prefetch を <head> に注入する。
   * スクリプトタグの src から API origin を推測し、重複注入は避ける。
   */
  function injectResourceHints() {
    if (document.getElementById('gai-agents-resource-hints')) return
    const marker = document.createElement('meta')
    marker.id = 'gai-agents-resource-hints'
    document.head.appendChild(marker)

    const scriptSrc = (thisScript as HTMLScriptElement | null)?.src || ''
    const studioOrigin = scriptSrc ? new URL(scriptSrc).origin : 'https://studio.moovy.jp'
    const apiOrigin = studioOrigin.replace('://studio.', '://api.studio.')

    const hints: Array<[string, string, boolean?]> = [
      ['preconnect', studioOrigin, true],
      ['preconnect', apiOrigin, true],
      ['preconnect', 'https://fonts.googleapis.com'],
      ['preconnect', 'https://fonts.gstatic.com', true],
      // 動画/サムネイルは CloudFront 経由（ドメインは実行時に解決されるため dns-prefetch 止まり）
      ['dns-prefetch', 'https://d23qwxdxnclxtr.cloudfront.net'],
    ]

    for (const [rel, href, crossorigin] of hints) {
      const link = document.createElement('link')
      link.rel = rel
      link.href = href
      if (crossorigin) link.crossOrigin = 'anonymous'
      document.head.appendChild(link)
    }
  }

  /**
   * スクリプトタグからシナリオIDを取得
   */
  function getScenarioId(): string | null {
    if (!thisScript) return null
    return (
      thisScript.getAttribute('data-scenario-id') ??
      // 後方互換性
      thisScript.getAttribute('data-script-id')
    )
  }

  /**
   * POPUP 型表示でクレジットに当てる style。
   *
   * POPUP 型は本体（サムネイル）が `position: fixed` で画面右下に切り離されるため、
   * Light DOM のスクリプト位置に置いたままだとクレジットだけが取り残される
   * （固定ヘッダーが無いサイトではページ上部に露出してしまう）。
   * そこでサムネイルと同じ右下に、サムネイル円の下側へ配置する。
   * サムネイル位置調整用の CSS 変数（--moovy-popup-bottom / --moovy-popup-right）を
   * 共有することで、サイト側のオフセット上書きにも追従する。
   */
  const CREDIT_POPUP_STYLE = [
    'all: revert',
    // インライン時と同じ flex 行（Powered by + ロゴ）を維持しつつ右寄せにする
    'display: flex !important',
    'align-items: center !important',
    // サムネイル円（96px）と同じ横幅の枠を円の真上に重ね、中央寄せでロゴを円の中心に合わせる
    'justify-content: center !important',
    'gap: 4px !important',
    'position: fixed !important',
    // 既定値は rem ではなく px。ここは Light DOM に直接当てるインラインスタイルで、
    // 埋め込み CSS のビルド時 rem → px 変換（postcss/rem-to-px.js）を通らない。
    // rem のままだと `html { font-size: 62.5% }` のサイトでサムネイルとズレるため px 必須。
    // FloatingVideoThumbnail 側の既定値（32px / 24px）と揃える。
    'right: var(--moovy-popup-right, 24px) !important',
    // サムネイル円の下側に出す（円の下端＝--moovy-popup-bottom より少し下に重ねる）
    'bottom: max(0px, calc(var(--moovy-popup-bottom, 32px) - 16px)) !important',
    'left: auto !important',
    'width: 96px !important',
    'margin: 0 !important',
    'padding: 0 !important',
    'font-size: 10px !important',
    'line-height: 1.4 !important',
    'letter-spacing: normal !important',
    'color: #999 !important',
    'text-decoration: none !important',
    'background: none !important',
    'border: none !important',
    // shadowHost（2147483647）の直下。サイトの sticky 等には確実に勝つ。
    'z-index: 2147483646 !important',
    'pointer-events: auto !important',
  ].join('; ')

  /**
   * POPUP 型クレジットの配置と、子要素の表示をまとめて適用する。
   * 固定サムネイル下では横幅が限られるため、表示は moovy studio ロゴのみにする。
   */
  const applyPopupCreditPlacement = (link: HTMLAnchorElement) => {
    link.setAttribute('style', CREDIT_POPUP_STYLE)
    const text = link.querySelector('span')
    if (text) text.style.setProperty('display', 'none', 'important')
    const logo = link.querySelector('img')
    if (logo) logo.style.setProperty('height', '12px', 'important')
  }

  /**
   * BOX / WIDE のクレジットを、ロゴを主張させず静かに置く。
   *
   * 横位置は既定（injectCreditLink の justify-content: center）と同じ中央のまま。
   * かつては max-width をカード幅の実測値でハードコードして右寄せしていたが、
   * 導入先が CSS で枠を狭めるとカードとズレるうえ、カード幅を変えるたびに
   * この値を直す必要があった。
   *
   * クレジットはコンテナの兄弟として width:100%（＝親と同じ幅）で入るので、
   * カード側を中央寄せ（BoxEntryBanner の margin-inline: auto）にしておけば
   * 両者が同じ軸で中央に揃う。固定値を持たないので、枠幅が変わっても崩れない。
   */
  const applyPolishedInlineCreditPlacement = (link: HTMLAnchorElement) => {
    link.style.setProperty('justify-content', 'center', 'important')
    link.style.setProperty('width', '100%', 'important')
    link.style.setProperty('box-sizing', 'border-box', 'important')
    link.style.setProperty('gap', '3px', 'important')
    link.style.setProperty('padding', '10px 3px 0', 'important')
    link.style.setProperty('font-size', '9px', 'important')

    const text = link.querySelector('span')
    if (text) {
      text.style.setProperty('font-size', '9px', 'important')
      text.style.setProperty('color', '#94a3b8', 'important')
    }
    const logo = link.querySelector('img')
    if (logo) {
      logo.style.setProperty('height', '11px', 'important')
      // 暗い背景では白ロゴに替えているので、薄くしすぎると背景に沈む
      logo.style.setProperty('opacity', link.hasAttribute('data-moovy-credit-on-dark') ? '0.7' : '0.45', 'important')
    }
  }

  /**
   * 要素の背後に見えている背景色が暗いかを判定する。
   *
   * 祖先をたどり、最初に見つかった不透明な background-color の明るさで判断する。
   * 背景が画像だけで色の指定がないサイトは判定できないため、
   * 従来どおり「明るい背景」として扱う（黒ロゴのまま）。
   */
  function isOnDarkBackground(element: Element | null): boolean {
    for (let node = element; node; node = node.parentElement) {
      const match = getComputedStyle(node).backgroundColor.match(/^rgba?\(([^)]+)\)$/)
      if (!match) continue
      const [r, g, b, alpha = 1] = match[1].split(/[\s,/]+/).filter(Boolean).map(Number)
      if (alpha === 0) continue
      // 知覚輝度（ITU-R BT.601）が中間より低ければ暗い背景とみなす
      return 0.299 * r + 0.587 * g + 0.114 * b < 128
    }
    return false
  }

  /**
   * Powered by moovy のクレジット表記を Light DOM に注入する
   *
   * - 埋め込みコンテナごとに 1 つ出力する（同じコンテナに二重生成しないようガード）。
   *   かつてはページ単位で 1 つだけだったが、複数の埋め込みが 1 つの要素を共有するため
   *   displayType が確定するたびに配置が上書きされ、後から確定した埋め込みの流儀へ
   *   持って行かれていた（POPUP が確定すると position:fixed で画面右下へ移動し、
   *   BOX/TILE のカード下からクレジットが消える）。utm_content も最後の確定が勝つため
   *   設置面別の計測が壊れる。コンテナ単位にすることで、各埋め込みが自分の真下に
   *   自分のクレジットを持ち、計測も設置面ごとに正しく記録される。
   * - 導入先サイトのCSS干渉を防ぐため style="all:revert" + !important で防御
   * - target="_blank" + rel="noopener"（nofollow は付けない）
   * - 「Powered by」テキスト + moovy studio ロゴ画像（動画個別ページと同デザイン）
   * - 既定はインライン表示想定（スクリプト位置）。POPUP 型のときは呼び出し側が
   *   displayType 確定後に CREDIT_POPUP_STYLE で配置を補正する。
   *
   * @param tenantId 流入計測用（utm_term）。data-tenant-id 未指定時は付与されない
   * @returns 注入した（または既存の）クレジット要素。後から配置を補正するために返す。
   */
  function injectCreditLink(container: HTMLElement, tenantId: string | undefined): HTMLAnchorElement | null {
    // このコンテナのクレジットは必ず直後の兄弟に入れているので、そこだけを見れば足りる。
    // document 全体を探すと他の埋め込みのクレジットを掴んでしまう。
    const sibling = container.nextElementSibling
    if (sibling instanceof HTMLAnchorElement && sibling.hasAttribute('data-moovy-credit')) {
      return sibling
    }

    const link = document.createElement('a')
    link.setAttribute('data-moovy-credit', '')
    // 表示タイプ確定前なので暫定の 'embed'。確定後に embed_inline / embed_popup へ差し替える
    link.href = buildWatermarkUrl('embed', { tenantId })
    link.target = '_blank'
    link.rel = 'noopener'
    link.setAttribute('aria-label', 'Powered by moovy studio')
    // 導入先サイトのCSSをリセットしたうえで、最小限のスタイルを !important で当てる
    // 注: 表示タイプごとに Shadow DOM 内のbottom paddingが異なるため、
    //    負マージンは使わず padding のみで間隔を保つ（めり込み防止）
    // 動画個別ページの「Powered by + ロゴ画像」デザインに合わせる
    link.setAttribute(
      'style',
      [
        'all: revert',
        'display: flex !important',
        'align-items: center !important',
        'justify-content: center !important',
        'gap: 4px !important',
        'font-size: 11px !important',
        'line-height: 1.4 !important',
        'letter-spacing: normal !important',
        'color: #999 !important',
        'text-decoration: none !important',
        'padding: 4px 0 !important',
        'margin: 0 !important',
        'width: 100% !important',
        'background: none !important',
        'border: none !important',
      ].join('; '),
    )

    // 「Powered by」テキスト
    const text = document.createElement('span')
    text.textContent = 'Powered by'
    text.setAttribute(
      'style',
      [
        'all: revert',
        'color: #999 !important',
        'font-size: 11px !important',
        'line-height: 1.4 !important',
        'letter-spacing: normal !important',
      ].join('; '),
    )

    // moovy studio ロゴ画像（既定は dark ロゴ。暗い背景なら挿入後に white ロゴへ切り替える）
    const logo = document.createElement('img')
    logo.src = 'https://studio.moovy.jp/images/logo-moovystudio-dark.png'
    logo.alt = 'moovy studio'
    logo.width = 84
    logo.height = 14
    logo.setAttribute(
      'style',
      [
        'all: revert',
        'display: inline-block !important',
        'height: 14px !important',
        'width: auto !important',
        'max-width: none !important',
        'vertical-align: middle !important',
        // 「Powered by」テキスト(#999)と馴染ませるため少し透過して落ち着かせる
        'opacity: 0.6 !important',
      ].join('; '),
    )

    link.appendChild(text)
    link.appendChild(logo)

    container.parentNode?.insertBefore(link, container.nextSibling)

    // 背景色は配置後でないと取れない。暗い背景では dark ロゴが背景に溶けて見えなくなる
    // main() はスクリプト読込直後に同期実行されることがあるため、ここでは後方の const を参照しない
    if (isOnDarkBackground(link.parentElement)) {
      link.setAttribute('data-moovy-credit-on-dark', '')
      logo.src = 'https://studio.moovy.jp/images/logo-moovystudio-white.png'
      logo.style.setProperty('opacity', '0.8', 'important')
    }
    return link
  }
})()
